import type { MutationType } from 'generated/gql';
import { DocumentLabel, DocumentType } from 'generated/gql';
import { t } from '@lingui/macro';
import FileSaver from 'file-saver';
import { upperFirst } from 'lodash';

import { mutationLabelObject } from './formatMutation';
import { capitalizeString } from './formatShares';

export const downloadFile = async (bytes: Uint8Array | Blob, name: string, mimeType?: string) => {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  const blob =
    bytes instanceof Blob
      ? bytes
      : new Blob([bytes as Uint8Array<ArrayBuffer>], {
          type: mimeType || 'application/octet-stream',
        });

  FileSaver.saveAs(blob, name);
};

// The file name is stored server-side as part of the upload itself, so a name given in
// the form is applied by uploading the content under that name.
export const renameFile = (content: File, name?: string | null) => {
  const fileName = name?.trim();
  if (!fileName || fileName === content.name) return content;

  return new File([content], fileName, {
    type: content.type,
    lastModified: content.lastModified,
  });
};

export const isUnsupportedFileError = (message: string) => {
  return (
    message.match(/^Content type (.*) is not allowed$/) ||
    message.match(/^Filetype (.*) is not allowed$/) ||
    message === 'Content type is empty'
  );
};

const documentLabels = {
  [DocumentLabel.ArticleOfAssociation]: () => t`statuten`,
  [DocumentLabel.CocExtract]: () => '',
  [DocumentLabel.ShareholdersAgreement]: () => '',
  [DocumentLabel.ShareholdersRegister]: () => '',
  [DocumentLabel.ShareholdersResolution]: () => '',
  [DocumentLabel.TrustConditions]: () => t`administratievoorwaarden`,
};
export const formatDocumentLabel = (documentLabel: DocumentLabel) => {
  return upperFirst(documentLabels[documentLabel]());
};

const documentTypes = {
  [DocumentType.PrivateDeed]: () => t`PrivateDeed`,
  [DocumentType.NotarialDeed]: () => t`NotarialDeed`, // other notarial deed
  [DocumentType.Other]: () => t`Document`, // other document
};

export const formatDocumentType = (
  documentType: DocumentType,
  mutationType: MutationType | null = null,
) => {
  if (mutationType !== null) {
    const mutationObject = mutationLabelObject[mutationType];
    if (documentType === DocumentType.NotarialDeed && mutationObject.deedLabel)
      return upperFirst(mutationObject.deedLabel());
    if (mutationObject.documentLabel) return upperFirst(mutationObject.documentLabel());
  }
  return upperFirst(documentTypes[documentType]());
};

export const documentTypeOptions = () =>
  Object.keys(documentTypes)
    .map(
      (value) =>
        ({
          value: value as keyof typeof documentTypes,
          label: capitalizeString(formatDocumentType(value as keyof typeof documentTypes)),
        }) as const,
    )
    .sort((a, b) => a.label.localeCompare(b.label));
